import React, { useEffect, useState } from "react";
import { apiService } from "@/api/apiService";
import { PlanWithSteps, StepStatus } from "@/models/plan";
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Body1,
    Button,
    Caption1,
    Spinner,
    Tag
} from "@fluentui/react-components";
import { CheckmarkRegular, DismissRegular } from "@fluentui/react-icons";

interface PlanCardProps {
    planId: string;
    sessionId: string;
    onPlanCompleted?: () => void;
    hasResultMessage?: boolean;
}

/**
 * PlanCard - Inline component for displaying plan steps within conversation
 */
const PlanCard: React.FC<PlanCardProps> = ({ planId, sessionId, onPlanCompleted, hasResultMessage }) => {
    const [plan, setPlan] = useState<PlanWithSteps | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [hasNotifiedCompletion, setHasNotifiedCompletion] = useState(false);

    useEffect(() => {
        loadPlanData();
        // Poll for updates every 2 seconds
        const interval = setInterval(loadPlanData, 2000);
        return () => clearInterval(interval);
    }, [planId]);

    // Reset notification flag when result message appears
    useEffect(() => {
        if (hasResultMessage) {
            setHasNotifiedCompletion(true);
        }
    }, [hasResultMessage]);

    const loadPlanData = async () => {
        try {
            const result = await apiService.getPlanById(planId, false);
            if (result && result.plan_with_steps) {
                // Backend returns PlanWithSteps which includes steps
                setPlan(result.plan_with_steps);

                // Check if all steps are completed
                const allStepsCompleted = result.plan_with_steps.steps?.every(
                    step => step.status === StepStatus.COMPLETED
                );

                // Notify parent if plan is completed, we haven't notified yet, and there's no result message yet
                if (allStepsCompleted && !hasNotifiedCompletion && !hasResultMessage && onPlanCompleted) {
                    setHasNotifiedCompletion(true);
                    // Add a small delay to ensure backend has created the conversation message
                    setTimeout(() => {
                        onPlanCompleted();
                    }, 500);
                }
            }
            setLoading(false);
        } catch (err) {
            console.error("Error loading plan:", err);
            setError("Failed to load plan details");
            setLoading(false);
        }
    };

    const handleApprove = async (stepId: string) => {
        try {
            await apiService.stepStatus(planId, sessionId, true, stepId);
            await loadPlanData(); // Reload to get updated status
        } catch (err) {
            console.error("Error approving step:", err);
        }
    };

    const handleReject = async (stepId: string) => {
        try {
            await apiService.stepStatus(planId, sessionId, false, stepId);
            await loadPlanData(); // Reload to get updated status
        } catch (err) {
            console.error("Error rejecting step:", err);
        }
    };

    const getStepStatusTag = (status: StepStatus, requiresApproval: boolean) => {
        switch (status) {
            case StepStatus.COMPLETED:
                return <Tag appearance="filled" color="success" size="small">Completed</Tag>;
            case StepStatus.AWAITING_FEEDBACK:
                return <Tag appearance="filled" color="informative" size="small">Awaiting Feedback</Tag>;
            case StepStatus.APPROVED:
                return <Tag appearance="filled" color="brand" size="small">Approved</Tag>;
            case StepStatus.REJECTED:
                return <Tag appearance="filled" color="danger" size="small">Rejected</Tag>;
            case StepStatus.ACTION_REQUESTED:
                return <Tag appearance="filled" color="warning" size="small">Action Requested</Tag>;
            case StepStatus.FAILED:
                return <Tag appearance="filled" color="danger" size="small">Failed</Tag>;
            case StepStatus.PLANNED:
                if (requiresApproval) {
                    return <Tag appearance="filled" color="warning" size="small">Awaiting Approval</Tag>;
                }
                return <Tag appearance="outline" size="small">Planned</Tag>;
            default:
                return <Tag appearance="outline" size="small">{status}</Tag>;
        }
    };

    if (loading) {
        return (
            <div style={{ padding: "1rem", border: "1px solid #e0e0e0", borderRadius: "8px", marginTop: "0.5rem" }}>
                <Spinner size="tiny" label="Loading plan..." />
            </div>
        );
    }

    if (error || !plan) {
        return (
            <div style={{ padding: "1rem", border: "1px solid #e0e0e0", borderRadius: "8px", marginTop: "0.5rem", backgroundColor: "#fff3cd" }}>
                <Body1>{error || "Plan not found"}</Body1>
            </div>
        );
    }

    return (
        <div style={{
            marginTop: "1rem",
            border: "1px solid #0078d4",
            borderRadius: "8px",
            backgroundColor: "#3d3e78",
            overflow: "hidden"
        }}>
            <div style={{ padding: "1rem", backgroundColor: "#444791", color: "white" }}>
                <Body1 style={{ fontWeight: 600, margin: 0 }}>
                    Plan: {plan.summary || "Executing actions..."}
                </Body1>
                <Caption1 style={{ color: "rgba(255, 255, 255, 0.9)" }}>
                    {plan.steps?.length || 0} step{plan.steps?.length !== 1 ? "s" : ""}
                </Caption1>
            </div>

            <div style={{ padding: "1rem" }}>
                <Accordion collapsible>
                    {plan.steps?.map((step, index) => (
                        <AccordionItem key={step.id} value={step.id}>
                            <AccordionHeader
                                expandIconPosition="end"
                                size="small"
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", width: "100%" }}>
                                    <Body1 style={{ fontSize: "14px", fontWeight: 500 }}>
                                        Step {index + 1}: {step.action?.substring(0, 60)}{step.action && step.action.length > 60 ? "..." : ""}
                                    </Body1>
                                    {getStepStatusTag(step.status, step.requires_approval ?? true)}
                                </div>
                            </AccordionHeader>
                            <AccordionPanel>
                                <div style={{ padding: "0.5rem 0" }}>
                                    <Body1 style={{ fontSize: "13px", marginBottom: "0.25rem" }}>
                                        <strong>Action:</strong> {step.action}
                                    </Body1>
                                    <Body1 style={{ fontSize: "13px", marginBottom: "0.5rem" }}>
                                        <strong>Agent:</strong> {step.agent}
                                    </Body1>

                                    {step.status === StepStatus.PLANNED && step.requires_approval && (
                                        <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.5rem" }}>
                                            <Button
                                                appearance="primary"
                                                size="small"
                                                icon={<CheckmarkRegular />}
                                                onClick={() => handleApprove(step.id)}
                                            >
                                                Approve
                                            </Button>
                                            <Button
                                                appearance="outline"
                                                size="small"
                                                icon={<DismissRegular />}
                                                onClick={() => handleReject(step.id)}
                                            >
                                                Reject
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </AccordionPanel>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </div>
    );
};

export default PlanCard;
